import classNames from 'classnames';
import { ComponentChild, h, JSX } from 'preact';

export type CellProps = JSX.IntrinsicElements['td'];

export interface BaseRow {
    key?: string;
    rowProps?: JSX.HTMLAttributes<HTMLTableRowElement>;
}

export interface Column<Row extends BaseRow> {
    header: string;
    headerClass?: string;
    render: (row: Row) => ComponentChild;
    getClass?: (row: Row) => string;
    getProps?: (row: Row) => CellProps;
}

export interface TableClasses<Row> {
    table?: string;
    header?: string;
    headerCell?: string;
    headerCellFirst?: string;
    headerCellLast?: string;
    body?: string;
    bodyRow?: string;
    getBodyRow?: (row: Row) => string;
    bodyCell?: string;
    bodyCellFirst?: string;
    bodyCellLast?: string;
}

export interface TableProps<Row extends BaseRow> {
    classes?: TableClasses<Row>;
    columns: Column<Row>[];
    rows: Row[];
    noHeader?: boolean;
}

export const Table = (props: TableProps<BaseRow>): JSX.Element => (
    <table class={props.classes?.table}>
        {!props.noHeader && (
            <thead class={props.classes?.header}>
                <tr>
                    {props.columns.map((column, index) => {
                        const classes = [
                            props.classes?.headerCell,
                            column.headerClass,
                        ];

                        if (index === 0) {
                            classes.push(props.classes?.headerCellFirst);
                        }

                        if (index === props.columns.length - 1) {
                            classes.push(props.classes?.headerCellLast);
                        }

                        return <th className={classNames(classes)}>{column.header}</th>;
                    })}
                </tr>
            </thead>
        )}
        <tbody class={props.classes?.body}>
            {props.rows.map((row) => {
                const cells = [];
                let toSkip = 0;

                for (const [index, column] of props.columns.entries()) {
                    if (toSkip) {
                        toSkip -= 1;
                        continue;
                    }

                    const cellProps = column.getProps?.(row);
                    toSkip = (cellProps?.colSpan as number ?? 1) - 1;

                    const classes = [
                        props.classes?.bodyCell,
                        column.getClass?.(row),
                    ];

                    if (index === 0) {
                        classes.push(props.classes?.bodyCellFirst);
                    }

                    if (index + toSkip >= props.columns.length - 1) {
                        classes.push(props.classes?.bodyCellLast);
                    }

                    cells.push(
                        <td class={classNames(classes)} {...cellProps}>
                            {column.render(row)}
                        </td>,
                    );
                }

                return (
                    <tr
                        key={row.key}
                        class={classNames(props.classes?.bodyRow, props.classes?.getBodyRow?.(row))}
                        {...row.rowProps}
                    >
                        {cells}
                    </tr>
                );
            })}
        </tbody>
    </table>
);
